import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import {
  FaChartLine,
  FaChevronDown,
  FaCheckCircle,
  FaCopy,
  FaDollarSign,
  FaExclamationCircle,
  FaInfoCircle,
  FaLink,
  FaMousePointer,
  FaUsers,
} from 'react-icons/fa';
import useUser from '../../hooks/useUser';
import SocialShareButtons from '../../components/SocialShareButtons';
import { getAffiliateCodeForApi } from '../../utils/utmTracker';
import affiliateHeroImage from '../../assets/botspot_affiliate_hero.webp';
import './AffiliatePage.css';

const API_URL = process.env.REACT_APP_API_URL;
const AFFILIATE_JOIN_INTENT_KEY = 'botspot_affiliate_join_intent';
const AFFILIATE_DASHBOARD_PATH = '/affiliate/dashboard';
const AFFILIATE_LANDING_PATH = '/affiliate';
const AFFILIATE_HERO_PRELOAD_ID = 'botspot-affiliate-hero-preload';
const AFFILIATE_PAYOUT_SUPPORT_CALL_URL = 'https://calendly.com/lumiwealth/botspot-call?utm_source=affiliate_dashboard&utm_medium=payout_support&utm_campaign=affiliate_payout_country';

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const TIMELINE_CHART = { width: 720, height: 220, top: 24, right: 28, bottom: 36, left: 36 };
const AFFILIATE_PROFILE_RETRY_DELAYS_MS = process.env.NODE_ENV === 'test' ? [] : [700, 1600];

function getLatestBankPayoutNotice(payout) {
  if (!payout?.id) return null;
  const amount = money(payout.amountCents);
  const status = String(payout.status || '').toLowerCase();
  if (status === 'failed') {
    return {
      tone: 'warning',
      text: `Latest bank payout ${amount} failed. Check payout details or contact support.`,
    };
  }
  if (status === 'paid') {
    return {
      tone: 'success',
      text: `Latest bank payout ${amount} is expected to be available in your bank account.`,
    };
  }
  if (status === 'pending' || status === 'in_transit') {
    return {
      tone: 'success',
      text: `Latest bank payout ${amount} is ${status.replace(/_/g, ' ')}.`,
    };
  }
  return null;
}

function preloadAffiliateHeroImage() {
  if (typeof document === 'undefined' || document.getElementById(AFFILIATE_HERO_PRELOAD_ID)) {
    return;
  }

  const link = document.createElement('link');
  link.id = AFFILIATE_HERO_PRELOAD_ID;
  link.rel = 'preload';
  link.as = 'image';
  link.href = affiliateHeroImage;
  link.type = 'image/webp';
  link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
}

preloadAffiliateHeroImage();

function getCurrentPathname() {
  if (typeof window === 'undefined') return '/affiliate';
  const pathname = typeof window.location?.pathname === 'string' ? window.location.pathname : '/affiliate';
  return pathname.replace(/\/+$/, '') || '/';
}

function getStripeReturnState() {
  if (typeof window === 'undefined') return { returned: false, refreshed: false };
  const search = typeof window.location?.search === 'string' ? window.location.search : '';
  const params = new URLSearchParams(search);
  return {
    returned: params.get('stripe_connect_return') === '1',
    refreshed: params.get('stripe_connect_refresh') === '1',
  };
}

function clearStripeReturnParams() {
  if (typeof window === 'undefined') return;
  const href = typeof window.location?.href === 'string'
    ? window.location.href
    : `${window.location?.origin || 'http://localhost'}${getCurrentPathname()}`;
  const url = new URL(href, window.location?.origin || 'http://localhost');
  url.searchParams.delete('stripe_connect_return');
  url.searchParams.delete('stripe_connect_refresh');
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

function wait(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function shouldRetryAffiliateProfileResponse(response) {
  return response.status === 408 || response.status === 429 || response.status >= 500;
}

async function fetchAffiliateProfileWithRetry(url, options = {}) {
  let lastError = null;
  for (let attempt = 0; attempt <= AFFILIATE_PROFILE_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const response = await fetch(url, options);
      if (response.ok || !shouldRetryAffiliateProfileResponse(response) || attempt === AFFILIATE_PROFILE_RETRY_DELAYS_MS.length) {
        return response;
      }
    } catch (error) {
      lastError = error;
      if (attempt === AFFILIATE_PROFILE_RETRY_DELAYS_MS.length) {
        throw error;
      }
    }
    await wait(AFFILIATE_PROFILE_RETRY_DELAYS_MS[attempt]);
  }
  throw lastError || new Error('Unable to load affiliate details.');
}

const affiliateBenefits = [
  {
    title: 'Up to 20% direct rewards',
    text: 'Earn when customers buy through your link or promo code.',
    icon: FaDollarSign,
  },
  {
    title: 'Invite affiliates',
    text: 'Earn when affiliates you recruit bring in sales.',
    icon: FaUsers,
  },
  {
    title: 'Share a promo code',
    text: 'Give buyers a discount while keeping attribution tied to you.',
    icon: FaLink,
  },
  {
    title: 'Lifetime customer credit',
    text: 'Future eligible purchases keep counting for you.',
    icon: FaChartLine,
  },
];

const affiliatePromoHighlights = [
  {
    label: 'Affiliate link',
    title: 'Tracks visitors automatically',
    text: 'Share your link anywhere. When someone signs up through it, future eligible purchases stay connected to you.',
    icon: FaLink,
  },
  {
    label: 'Promo code',
    title: 'Buyers save 10%, you earn 10%',
    text: 'Share your code like an influencer offer. When a buyer uses it at checkout, the sale is credited directly to you.',
    icon: FaDollarSign,
  },
  {
    label: 'Simple tracking',
    title: 'Link visits and code sales stay clear',
    text: 'Use your link for visitor tracking and your promo code for checkout discounts. Both connect sales back to your affiliate profile.',
    icon: FaChartLine,
  },
];

const affiliateWorkflowSections = [
  {
    label: 'Direct referrals',
    title: 'Earn up to 20% when your own referral becomes a paying customer.',
    text: 'Share your BotSpot affiliate link with traders, investors, and market-focused audiences. When someone signs up through your link and completes a paid purchase, BotSpot records the customer under your affiliate profile and creates a direct reward from that payment.',
    icon: FaDollarSign,
  },
  {
    label: 'Promo codes',
    title: 'Give buyers 10% off and earn 10% on code sales.',
    text: 'Your affiliate profile also includes a promo code. If a buyer uses that code at checkout, they save immediately and BotSpot credits the purchase directly to your affiliate profile.',
    icon: FaLink,
  },
  {
    label: 'Recruited affiliates',
    title: 'Earn when an affiliate you invited brings in sales.',
    text: 'Your same link can invite another affiliate. If that person joins the affiliate program through your link, they become connected to you as a recruited affiliate, and eligible sales they bring can also reward you.',
    icon: FaUsers,
  },
  {
    label: 'Lifetime credit',
    title: 'Keep credit for customers you directly refer.',
    text: 'After a customer is attributed to your affiliate link, future eligible purchases from that same customer continue to count toward your affiliate earnings. The first valid affiliate relationship is preserved, so you do not have to keep re-sending a link before every renewal or future purchase.',
    icon: FaChartLine,
  },
  {
    label: 'Payouts',
    title: 'Commissions are tracked first, then paid after payout setup is ready.',
    text: 'You can share your link before payout setup is complete. Once a commission is created, BotSpot keeps it pending until your payout account is ready, then transfers eligible earnings automatically.',
    icon: FaDollarSign,
  },
];

export default function AffiliatePage() {
  const { isAuthenticated, isLoading, getApiToken } = useUser();
  const [currentPath, setCurrentPath] = useState(getCurrentPathname);
  const [profile, setProfile] = useState(null);
  const [totals, setTotals] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [profileReady, setProfileReady] = useState(false);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [creating, setCreating] = useState(false);
  const [startingOnboarding, setStartingOnboarding] = useState(false);
  const [showPayoutCountryPanel, setShowPayoutCountryPanel] = useState(false);
  const [payoutCountries, setPayoutCountries] = useState([]);
  const [loadingPayoutCountries, setLoadingPayoutCountries] = useState(false);
  const [selectedPayoutCountry, setSelectedPayoutCountry] = useState('');
  const [payoutCountryDropdownOpen, setPayoutCountryDropdownOpen] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const payoutCountryDropdownRef = useRef(null);

  useEffect(() => {
    const syncPath = () => setCurrentPath(getCurrentPathname());
    window.addEventListener('popstate', syncPath);
    return () => window.removeEventListener('popstate', syncPath);
  }, []);

  const navigateToDashboard = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (getCurrentPathname() !== AFFILIATE_DASHBOARD_PATH) {
      window.history.pushState({}, '', AFFILIATE_DASHBOARD_PATH);
    }
    setCurrentPath(AFFILIATE_DASHBOARD_PATH);
  }, []);

  const navigateToAffiliateLanding = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (getCurrentPathname() !== AFFILIATE_LANDING_PATH) {
      window.history.pushState({}, '', AFFILIATE_LANDING_PATH);
    }
    setCurrentPath(AFFILIATE_LANDING_PATH);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setProfile(null);
      setTotals(null);
      setTimeline([]);
      setProfileLoadFailed(false);
      setError('');
      setNotice('');
      setCreating(false);
      setStartingOnboarding(false);
      setShowPayoutCountryPanel(false);
      setProfileReady(false);
      setLoadingProfile(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!notice && !error) return undefined;
    const timer = window.setTimeout(() => {
      setNotice('');
      setError('');
    }, 3500);
    return () => window.clearTimeout(timer);
  }, [notice, error]);

  useEffect(() => {
    if (!payoutCountryDropdownOpen) return undefined;

    const closeDropdown = (event) => {
      if (payoutCountryDropdownRef.current?.contains(event.target)) return;
      setPayoutCountryDropdownOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setPayoutCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', closeDropdown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', closeDropdown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [payoutCountryDropdownOpen]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!API_URL) {
      setError('Affiliate setup is temporarily unavailable.');
      setProfileLoadFailed(true);
      setProfileReady(true);
      navigateToDashboard();
      return;
    }
    let cancelled = false;

    async function loadProfile() {
      setLoadingProfile(true);
      setProfileReady(false);
      setProfileLoadFailed(false);
      setError('');
      try {
        const token = await getApiToken();
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await fetchAffiliateProfileWithRetry(`${API_URL}/affiliates/me`, {
          headers: authHeaders,
        });
        if (!response.ok) {
          throw new Error('Unable to load affiliate details.');
        }
        const data = await response.json();
        const shouldAutoCreate = window.sessionStorage.getItem(AFFILIATE_JOIN_INTENT_KEY) === 'true';
        if (!data.profile && shouldAutoCreate) {
          setCreating(true);
          const affiliateCode = getAffiliateCodeForApi();
          const createResponse = await fetch(`${API_URL}/affiliates/me`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders,
            },
            body: JSON.stringify(affiliateCode ? { affiliateCode } : {}),
          });
          const created = await createResponse.json();
          if (!createResponse.ok) {
            throw new Error(created?.message || 'Unable to create affiliate link.');
          }
          // The create request succeeded, so consume the one-time intent even if
          // a concurrent auth/profile refresh has already cancelled this effect.
          // Leaving it behind can submit the create flow again on the next load.
          window.sessionStorage.removeItem(AFFILIATE_JOIN_INTENT_KEY);
          if (!cancelled) {
            setProfile(created.profile || null);
            setTotals(created.totals || created.profile?.totals || null);
            setTimeline(Array.isArray(created.timeline) ? created.timeline : []);
            setProfileLoadFailed(false);
            setNotice('Link and Promo code Ready');
            navigateToDashboard();
          }
          return;
        }
        if (!cancelled) {
          let nextProfile = data.profile || null;
          let nextTotals = data.totals || null;
          let nextTimeline = Array.isArray(data.timeline) ? data.timeline : [];
          const stripeReturnState = getStripeReturnState();
          if (nextProfile && stripeReturnState.returned) {
            const syncResponse = await fetch(`${API_URL}/affiliates/me/stripe-account-sync`, {
              method: 'POST',
              headers: authHeaders,
            });
            const synced = await syncResponse.json();
            if (syncResponse.ok) {
              nextProfile = synced.profile || nextProfile;
              nextTotals = synced.totals || nextProfile?.totals || nextTotals;
              nextTimeline = Array.isArray(synced.timeline) ? synced.timeline : nextTimeline;
              setNotice('Payout setup updated.');
            }
            clearStripeReturnParams();
          } else if (stripeReturnState.refreshed) {
            clearStripeReturnParams();
          } else {
            setNotice('');
          }
          setProfile(nextProfile);
          setTotals(nextTotals);
          setTimeline(nextTimeline);
          setProfileLoadFailed(false);
          if (nextProfile) {
            navigateToDashboard();
          } else if (getCurrentPathname() === AFFILIATE_DASHBOARD_PATH) {
            navigateToAffiliateLanding();
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          setProfileLoadFailed(true);
          setError(loadError.message || 'Unable to load affiliate details.');
          navigateToDashboard();
        }
      } finally {
        if (!cancelled) {
          setLoadingProfile(false);
          setProfileReady(true);
          setCreating(false);
        }
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [getApiToken, isAuthenticated, navigateToAffiliateLanding, navigateToDashboard]);

  const affiliateUrl = useMemo(() => {
    if (!profile?.code || typeof window === 'undefined') return '';
    return `${window.location.origin}/?aff=${encodeURIComponent(profile.code)}`;
  }, [profile?.code]);
  const affiliatePromoCode = profile?.promoCode || '';
  const affiliatePromoDiscountPercent = Number(
    profile?.promoDiscountPercent ?? ((profile?.promoDiscountBps || 0) / 100),
  );
  const affiliatePromoDiscountLabel = Number.isFinite(affiliatePromoDiscountPercent) && affiliatePromoDiscountPercent > 0
    ? `${affiliatePromoDiscountPercent}% off`
    : 'buyer discount';
  const affiliateShareText = useMemo(() => {
    const promoText = affiliatePromoCode
      ? ` Use code ${affiliatePromoCode} for ${affiliatePromoDiscountLabel}.`
      : '';
    return `Explore BotSpot AI trading tools with my affiliate link.${promoText}`;
  }, [affiliatePromoCode, affiliatePromoDiscountLabel]);
  const isAffiliateSuspended = profile?.status === 'suspended';
  const stripeAccountStatus = profile?.stripeAccountStatus || 'not_started';
  const isStripePayoutReady =
    stripeAccountStatus === 'active' &&
    profile?.stripeTransfersEnabled &&
    profile?.stripePayoutsEnabled &&
    profile?.stripeDetailsSubmitted;
  const pendingCommissionCents = totals?.awaitingTransferCommissionCents ?? totals?.pendingCommissionCents ?? 0;
  const transferredCommissionCents = totals?.transferredCommissionCents ?? totals?.paidCommissionCents ?? 0;
  const failedCommissionCents = totals?.transferIssueCommissionCents ?? totals?.failedCommissionCents ?? 0;
  const commissionNeedingTransferCents = pendingCommissionCents + failedCommissionCents;
  const shouldPromptStripePayoutSetup =
    !isStripePayoutReady && !isAffiliateSuspended && commissionNeedingTransferCents > 0;
  const shouldShowPayoutSetupCard = !isStripePayoutReady && !isAffiliateSuspended;
  const payoutSetupCardDetail = shouldPromptStripePayoutSetup
    ? `${money(commissionNeedingTransferCents)} waiting. Complete setup to receive this commission.`
    : 'Required before commissions can be paid.';
  const payoutSetupCardActionLabel = shouldPromptStripePayoutSetup ? 'Complete setup' : 'Set up';
  const shouldShowTransferFailureAlert =
    isStripePayoutReady && !isAffiliateSuspended && failedCommissionCents > 0;
  const latestBankPayoutNotice = getLatestBankPayoutNotice(profile?.stripeLatestPayout);
  const clicks = totals?.clicks || profile?.clickCount || 0;
  const customers = totals?.customers || 0;
  const timelineChart = useMemo(() => {
    const points = (Array.isArray(timeline) ? timeline : [])
      .map((point) => ({
        label: point?.label || point?.date || '',
        totalReferrals: Number(point?.totalReferrals) || 0,
      }))
      .filter((point) => point.label);
    const hasData = points.some((point) => point.totalReferrals > 0);
    if (!hasData) return { hasData: false, path: '', areaPath: '', circles: [], gridLines: [], max: 0, total: 0 };

    const max = Math.max(...points.map((point) => point.totalReferrals), 1);
    const plotWidth = TIMELINE_CHART.width - TIMELINE_CHART.left - TIMELINE_CHART.right;
    const plotHeight = TIMELINE_CHART.height - TIMELINE_CHART.top - TIMELINE_CHART.bottom;
    const baselineY = TIMELINE_CHART.height - TIMELINE_CHART.bottom;
    const circles = points.map((point, index) => {
      const x = TIMELINE_CHART.left + (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
      const y = TIMELINE_CHART.top + plotHeight - (point.totalReferrals / max) * plotHeight;
      return { ...point, x, y };
    });
    const path = circles
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
      .join(' ');
    const areaPath = `${path} L ${circles[circles.length - 1].x.toFixed(1)} ${baselineY} L ${circles[0].x.toFixed(1)} ${baselineY} Z`;
    const gridLines = [0, 0.5, 1].map((ratio) => TIMELINE_CHART.top + plotHeight * ratio);
    return { hasData: true, path, areaPath, circles, gridLines, max, total: circles[circles.length - 1]?.totalReferrals || 0 };
  }, [timeline]);

  const startAuthSignup = () => {
    window.sessionStorage.setItem(AFFILIATE_JOIN_INTENT_KEY, 'true');
    const params = new URLSearchParams({
      returnTo: AFFILIATE_DASHBOARD_PATH,
      source: 'affiliate_join',
      intent: 'signup',
    });
    window.location.href = `/login?${params.toString()}`;
  };

  const joinAffiliateProgram = async () => {
    setCreating(true);
    setError('');
    setNotice('');
    try {
      if (!API_URL) throw new Error('Affiliate setup is temporarily unavailable.');
      const token = await getApiToken();
      const affiliateCode = getAffiliateCodeForApi();
      const response = await fetch(`${API_URL}/affiliates/me`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(affiliateCode ? { affiliateCode } : {}),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Unable to create affiliate link.');
      }
      setProfile(data.profile || null);
      setTotals(data.totals || data.profile?.totals || null);
      setTimeline(Array.isArray(data.timeline) ? data.timeline : []);
      setProfileLoadFailed(false);
      setNotice('Link and Promo code Ready');
      window.sessionStorage.removeItem(AFFILIATE_JOIN_INTENT_KEY);
      navigateToDashboard();
    } catch (createError) {
      setError(createError.message || 'Unable to create affiliate link.');
    } finally {
      setCreating(false);
    }
  };

  const loadPayoutCountries = useCallback(async () => {
    if (payoutCountries.length > 0 || loadingPayoutCountries) return payoutCountries;
    setLoadingPayoutCountries(true);
    try {
      if (!API_URL) throw new Error('Affiliate setup is temporarily unavailable.');
      const token = await getApiToken();
      const response = await fetch(`${API_URL}/affiliates/payout-countries`, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Unable to load payout countries.');
      }
      const countries = Array.isArray(data.countries) ? data.countries : [];
      setPayoutCountries(countries);
      return countries;
    } finally {
      setLoadingPayoutCountries(false);
    }
  }, [getApiToken, loadingPayoutCountries, payoutCountries]);

  const openPayoutCountryPanel = async () => {
    setError('');
    setNotice('');
    setSelectedPayoutCountry(profile?.stripePayoutCountry || '');
    setPayoutCountryDropdownOpen(false);
    setShowPayoutCountryPanel(true);
    try {
      await loadPayoutCountries();
    } catch (loadError) {
      setError(loadError.message || 'Unable to load payout countries.');
    }
  };

  const startStripeOnboarding = async (countryCode = '') => {
    setCreating(true);
    setStartingOnboarding(true);
    setError('');
    setNotice('');
    try {
      if (!API_URL) throw new Error('Affiliate setup is temporarily unavailable.');
      const token = await getApiToken();
      const body = countryCode ? { countryCode } : {};
      const response = await fetch(`${API_URL}/affiliates/me/stripe-onboarding-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Unable to create affiliate link.');
      }
      setProfile(data.profile || null);
      setTotals(data.totals || data.profile?.totals || null);
      setTimeline(Array.isArray(data.timeline) ? data.timeline : []);
      setProfileLoadFailed(false);
      setNotice('Opening payout setup.');
      setShowPayoutCountryPanel(false);
      window.sessionStorage.removeItem(AFFILIATE_JOIN_INTENT_KEY);
      if (data.url) {
        window.location.href = data.url;
        return;
      }
    } catch (createError) {
      setError(createError.message || 'Unable to start payout setup.');
    } finally {
      setCreating(false);
      setStartingOnboarding(false);
    }
  };

  const handlePayoutSetupClick = () => {
    if (!profile?.stripeConnectedAccountId) {
      openPayoutCountryPanel();
      return;
    }
    startStripeOnboarding();
  };

  const confirmPayoutCountry = () => {
    if (!selectedPayoutCountry) {
      setError('Choose your payout country before continuing.');
      return;
    }
    startStripeOnboarding(selectedPayoutCountry);
  };

  const selectedPayoutCountryOption = payoutCountries.find((country) => country.code === selectedPayoutCountry);

  const selectPayoutCountry = (countryCode) => {
    setSelectedPayoutCountry(countryCode);
    setPayoutCountryDropdownOpen(false);
  };

  const handlePayoutCountryTriggerKeyDown = (event) => {
    if (loadingPayoutCountries || startingOnboarding) return;
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setPayoutCountryDropdownOpen(true);
    }
  };

  const copyAffiliateUrl = async () => {
    if (isAffiliateSuspended || !affiliateUrl || typeof navigator === 'undefined') return;
    try {
      await navigator.clipboard.writeText(affiliateUrl);
      setNotice('Link copied.');
    } catch {
      setError('Unable to copy the affiliate link.');
    }
  };

  const copyAffiliatePromoCode = async () => {
    if (isAffiliateSuspended || !affiliatePromoCode || typeof navigator === 'undefined') return;
    try {
      await navigator.clipboard.writeText(affiliatePromoCode);
      setNotice('Promo code copied.');
    } catch {
      setError('Unable to copy the promo code.');
    }
  };

  const isResolvingAffiliateState = isLoading || loadingProfile || (isAuthenticated && !profileReady);
  const isDashboardRoute = currentPath === AFFILIATE_DASHBOARD_PATH;
  const shouldShowDashboard = Boolean(isDashboardRoute && (profile || profileLoadFailed));
  const shouldShowToast = Boolean((notice || error) && !(profileLoadFailed && !profile));
  const payoutCountryForm = showPayoutCountryPanel ? (
    <div className="affiliate-payout-country-form">
      <div className="affiliate-payout-country-field">
        <span id="affiliate-payout-country-label">Country</span>
        <div className="affiliate-payout-country-select" ref={payoutCountryDropdownRef}>
          <button
            type="button"
            className="affiliate-payout-country-trigger"
            role="combobox"
            aria-labelledby="affiliate-payout-country-label affiliate-payout-country-selected"
            aria-expanded={payoutCountryDropdownOpen}
            aria-controls="affiliate-payout-country-list"
            disabled={loadingPayoutCountries || startingOnboarding}
            onClick={() => setPayoutCountryDropdownOpen((isOpen) => !isOpen)}
            onKeyDown={handlePayoutCountryTriggerKeyDown}
          >
            <span id="affiliate-payout-country-selected">
              {loadingPayoutCountries
                ? 'Loading countries...'
                : selectedPayoutCountryOption?.name || 'Select country'}
            </span>
            {loadingPayoutCountries ? (
              <span className="affiliate-payout-country-inline-spinner" aria-hidden="true" />
            ) : (
              <FaChevronDown className="affiliate-payout-country-chevron" aria-hidden="true" />
            )}
          </button>
          {payoutCountryDropdownOpen && !loadingPayoutCountries && (
            <div className="affiliate-payout-country-menu" id="affiliate-payout-country-list" role="listbox">
              {payoutCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  role="option"
                  aria-selected={country.code === selectedPayoutCountry}
                  className="affiliate-payout-country-option"
                  onClick={() => selectPayoutCountry(country.code)}
                >
                  {country.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="affiliate-payout-country-note">
        Country missing? Keep sharing your link and code.{' '}
        <a href={AFFILIATE_PAYOUT_SUPPORT_CALL_URL} target="_blank" rel="noopener noreferrer">
          Book support call
        </a>
        {' '}for payout options.
      </p>
      <div className="affiliate-payout-country-actions">
        <button
          type="button"
          className="affiliate-secondary-button"
          onClick={() => setShowPayoutCountryPanel(false)}
          disabled={startingOnboarding}
        >
          Cancel
        </button>
        <button
          type="button"
          className="affiliate-primary-button affiliate-payout-country-submit"
          onClick={confirmPayoutCountry}
          disabled={!selectedPayoutCountry || loadingPayoutCountries || startingOnboarding}
        >
          {startingOnboarding ? 'Opening...' : 'Continue'}
        </button>
      </div>
    </div>
  ) : null;
  const renderPayoutSetupCard = ({ detail = 'Required before commissions can be paid.', actionLabel = 'Set up' } = {}) => (
    <div className={`affiliate-payout-setup-card ${showPayoutCountryPanel ? 'is-expanded' : ''}`}>
      <div className="affiliate-payout-setup-card-top">
        <div className="affiliate-action-card-main">
          {!showPayoutCountryPanel && (
            <div className="affiliate-action-card-icon" aria-hidden="true">
              <FaDollarSign />
            </div>
          )}
          <div>
            <span>Payout setup</span>
            {!showPayoutCountryPanel && <strong>{detail}</strong>}
          </div>
        </div>
        {!showPayoutCountryPanel && (
          <button
            className="affiliate-secondary-button"
            type="button"
            onClick={handlePayoutSetupClick}
            disabled={startingOnboarding}
          >
            {startingOnboarding ? 'Opening...' : actionLabel}
          </button>
        )}
      </div>
      {payoutCountryForm}
    </div>
  );

  const joinButton = isAuthenticated ? (
    <button
      className="affiliate-primary-button"
      type="button"
      onClick={joinAffiliateProgram}
      disabled={creating}
    >
      {creating ? (
        <>
          Creating
          <span className="affiliate-button-spinner" aria-hidden="true" />
        </>
      ) : (
        'Join Affiliate Program'
      )}
    </button>
  ) : (
    <button className="affiliate-primary-button" type="button" onClick={startAuthSignup}>
      Join Affiliate Program
    </button>
  );

  if (isResolvingAffiliateState) {
    return (
      <main className="affiliate-page">
        <div className="affiliate-page-loader" role="status" aria-label="Loading affiliate page">
          <Spinner animation="border" variant="light" />
        </div>
      </main>
    );
  }

  return (
    <main className="affiliate-page">
      <section className={`affiliate-shell ${shouldShowDashboard ? 'affiliate-shell-dashboard' : ''}`}>
        {shouldShowDashboard ? (
          <section className="affiliate-dashboard">
            <div className="affiliate-dashboard-header">
              <div>
                <h2>Dashboard</h2>
                <p>Track your referrals and get rewarded</p>
              </div>
            </div>
            {shouldShowToast && (
              <div className="affiliate-toast-stack" aria-live="polite">
                {notice && (
                  <div className="affiliate-toast affiliate-toast-success" role="status">
                    <span className="affiliate-toast-icon" aria-hidden="true">
                      <FaCheckCircle />
                    </span>
                    <span>{notice}</span>
                  </div>
                )}
                {error && (
                  <div className="affiliate-toast affiliate-toast-error" role="alert">
                    <span className="affiliate-toast-icon" aria-hidden="true">
                      <FaExclamationCircle />
                    </span>
                    <span>{error}</span>
                  </div>
                )}
              </div>
            )}
            {!profile ? (
              <div className="affiliate-dashboard-unavailable" role="status">
                <div className="affiliate-dashboard-unavailable-icon" aria-hidden="true">
                  <FaExclamationCircle />
                </div>
                <div>
                  <h3>Unable to load affiliate details.</h3>
                  <p>Check your connection and refresh this page.</p>
                  <button
                    type="button"
                    className="affiliate-unavailable-refresh"
                    onClick={() => window.location.reload()}
                  >
                    Refresh page
                  </button>
                </div>
              </div>
            ) : (
              <>
                {isAffiliateSuspended && (
                  <div className="affiliate-alert warning">
                    Your affiliate link is currently suspended. New affiliate customers will not be tracked.
                  </div>
                )}
                {shouldShowTransferFailureAlert && (
                  <div className="affiliate-alert warning affiliate-dashboard-warning affiliate-transfer-review-alert">
                    <FaExclamationCircle aria-hidden="true" />
                    <span>{money(failedCommissionCents)} transfer needs review. Check your payout details or contact support.</span>
                  </div>
                )}
                {latestBankPayoutNotice && (
                  <div className={`affiliate-alert ${latestBankPayoutNotice.tone} affiliate-dashboard-warning`}>
                    <FaInfoCircle aria-hidden="true" />
                    <span>{latestBankPayoutNotice.text}</span>
                  </div>
                )}
                <div className="affiliate-dashboard-summary">
                  <div className={`affiliate-link-strip ${isAffiliateSuspended ? 'is-suspended' : ''}`}>
                    <div>
                      <span>Affiliate link</span>
                      <strong>{isAffiliateSuspended ? 'Affiliate link suspended' : affiliateUrl || 'Link unavailable'}</strong>
                    </div>
                    <button
                      type="button"
                      onClick={copyAffiliateUrl}
                      disabled={isAffiliateSuspended || !affiliateUrl}
                      aria-label="Copy affiliate link"
                    >
                      <FaCopy aria-hidden="true" />
                    </button>
                  </div>
                  <div className={`affiliate-link-strip affiliate-promo-strip ${isAffiliateSuspended ? 'is-suspended' : ''}`}>
                    <div>
                      <span>Promo code</span>
                      <strong>{isAffiliateSuspended ? 'Promo code suspended' : affiliatePromoCode || 'Promo code unavailable'}</strong>
                      {!isAffiliateSuspended && affiliatePromoCode && (
                        <em>Buyers get {affiliatePromoDiscountLabel}. You earn 10% when their payment succeeds.</em>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={copyAffiliatePromoCode}
                      disabled={isAffiliateSuspended || !affiliatePromoCode}
                      aria-label="Copy affiliate promo code"
                    >
                      <FaCopy aria-hidden="true" />
                    </button>
                  </div>
                  {shouldShowPayoutSetupCard && (
                    renderPayoutSetupCard({
                      detail: payoutSetupCardDetail,
                      actionLabel: payoutSetupCardActionLabel,
                    })
                  )}
                  {!isAffiliateSuspended && affiliateUrl && (
                    <div className="affiliate-social-share-card">
                      <div className="affiliate-social-share-copy">
                        <span>Share</span>
                        <strong>Your invite</strong>
                        <p>Post your link or promo code.</p>
                      </div>
                      <SocialShareButtons
                        url={affiliateUrl}
                        text={affiliateShareText}
                        label=""
                        className="affiliate-social-share-actions"
                      />
                    </div>
                  )}
                  <div className="affiliate-dashboard-metrics">
                    <div className="affiliate-stat-card affiliate-stat-card-visits">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaMousePointer /></div>
                      <div>
                        <span>Visits</span>
                        <strong>{clicks}</strong>
                      </div>
                    </div>
                    <div className="affiliate-stat-card affiliate-stat-card-buyers">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaUsers /></div>
                      <div>
                        <span>Buyers</span>
                        <strong>{customers}</strong>
                      </div>
                    </div>
                    <div className="affiliate-stat-card affiliate-stat-card-pending">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaDollarSign /></div>
                      <div>
                        <span>Awaiting transfer</span>
                        <strong>{money(pendingCommissionCents)}</strong>
                      </div>
                    </div>
                    <div className="affiliate-stat-card affiliate-stat-card-paid">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaChartLine /></div>
                      <div>
                        <span>Transferred</span>
                        <strong>{money(transferredCommissionCents)}</strong>
                      </div>
                    </div>
                    <div className="affiliate-stat-card affiliate-stat-card-issues">
                      <div className="affiliate-stat-icon" aria-hidden="true"><FaExclamationCircle /></div>
                      <div>
                        <span>Transfer issues</span>
                        <strong>{money(failedCommissionCents)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="affiliate-timeline-card">
                    <div className="affiliate-timeline-header">
                      <div>
                        <h3>Total Referrals Over Time</h3>
                      </div>
                      {timelineChart.hasData ? (
                        <div className="affiliate-timeline-total">
                          <span>Total referrals</span>
                          <strong>{timelineChart.total}</strong>
                        </div>
                      ) : (
                        <FaInfoCircle aria-hidden="true" />
                      )}
                    </div>
                    <div className="affiliate-timeline-body">
                      {timelineChart.hasData ? (
                        <svg
                          className="affiliate-timeline-chart"
                          viewBox={`0 0 ${TIMELINE_CHART.width} ${TIMELINE_CHART.height}`}
                          role="img"
                          aria-label="Total referrals over time chart"
                        >
                          <defs>
                            <linearGradient id="affiliateTimelineStroke" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#ff8c00" />
                              <stop offset="54%" stopColor="#ffb347" />
                              <stop offset="100%" stopColor="#2ad18b" />
                            </linearGradient>
                            <linearGradient id="affiliateTimelineArea" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="#ff8c00" stopOpacity="0.16" />
                              <stop offset="100%" stopColor="#ff8c00" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          {timelineChart.gridLines.map((y) => (
                            <line
                              key={`grid-${y}`}
                              className="affiliate-timeline-grid-line"
                              x1={TIMELINE_CHART.left}
                              y1={y}
                              x2={TIMELINE_CHART.width - TIMELINE_CHART.right}
                              y2={y}
                            />
                          ))}
                          <line
                            x1={TIMELINE_CHART.left}
                            y1={TIMELINE_CHART.top}
                            x2={TIMELINE_CHART.left}
                            y2={TIMELINE_CHART.height - TIMELINE_CHART.bottom}
                          />
                          <path className="affiliate-timeline-area" d={timelineChart.areaPath} />
                          <path d={timelineChart.path} />
                          {timelineChart.circles.map((point) => (
                            <g key={`${point.label}-${point.x}`}>
                              <circle cx={point.x} cy={point.y} r="4" />
                              <text className="affiliate-timeline-point-value" x={point.x} y={Math.max(point.y - 10, 14)}>
                                {point.totalReferrals}
                              </text>
                              <text x={point.x} y={TIMELINE_CHART.height - 12}>{point.label}</text>
                            </g>
                          ))}
                        </svg>
                      ) : (
                        <div className="affiliate-timeline-empty">
                          <div className="affiliate-timeline-empty-icon" aria-hidden="true">
                            <FaChartLine />
                          </div>
                          <strong>No data to display</strong>
                          <span>Tracked referrals will appear here once visitors use your link or promo code.</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        ) : (
          <section className="affiliate-landing">
            <div className="affiliate-hero-card">
              <div className="affiliate-hero-copy">
                <span className="affiliate-kicker">
                  <span aria-hidden="true" />
                  BotSpot Affiliate
                </span>
                <h1>
                  <span className="affiliate-title-line affiliate-title-line-primary">
                    Earn Up to <span className="affiliate-title-percent">20%</span>
                  </span>
                  <span className="affiliate-title-line">by sharing</span>
                  <span className="affiliate-title-line affiliate-title-brand">BotSpot</span>
                </h1>
                <p>
                  Share your affiliate link for up to 20% rewards, or share your promo code so buyers save 10% and you earn 10% on code sales.
                </p>
                <div className="affiliate-hero-actions">
                  {joinButton}
                </div>
                {error && <div className="affiliate-alert error">{error}</div>}
              </div>
            </div>
            <div className="affiliate-benefit-grid">
              {affiliateBenefits.map((benefit) => {
                const BenefitIcon = benefit.icon;
                return (
                  <article className="affiliate-benefit-card" key={benefit.title}>
                    <BenefitIcon aria-hidden="true" />
                    <h2>{benefit.title}</h2>
                    <p>{benefit.text}</p>
                  </article>
                );
              })}
            </div>
            <div className="affiliate-promo-model-card">
              <div className="affiliate-promo-model-copy">
                <h2>Link and promo code work together</h2>
                <p>
                  Affiliates can share either one. The link handles visit tracking, while the code gives buyers a checkout discount and credits the sale directly to the code owner.
                </p>
              </div>
              <div className="affiliate-promo-model-grid">
                {affiliatePromoHighlights.map((item) => {
                  const PromoIcon = item.icon;
                  return (
                    <article className="affiliate-promo-model-item" key={item.label}>
                      <div className="affiliate-promo-model-heading">
                        <PromoIcon aria-hidden="true" />
                        <span>{item.label}</span>
                      </div>
                      <strong>{item.title}</strong>
                      <p>{item.text}</p>
                    </article>
                  );
                })}
              </div>
            </div>
            <div className="affiliate-steps-card">
              <h2>How it works</h2>
              <p className="affiliate-steps-intro">
                Share a link, offer a buyer discount code, and invite other affiliates from one simple dashboard.
              </p>
              <div className="affiliate-workflow">
                {affiliateWorkflowSections.map((section) => {
                  const WorkflowIcon = section.icon;
                  return (
                    <article className="affiliate-workflow-row" key={section.label}>
                      <div className="affiliate-workflow-heading">
                        <WorkflowIcon aria-hidden="true" />
                        <span>{section.label}</span>
                      </div>
                      <h3>{section.title}</h3>
                      <p>{section.text}</p>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </section>
    </main>
  );
}
